/**
 * Bill of Materials Schema: Shelf Code Definitions & Articles
 *
 * This schema stores the reference data from the Racking Guidelines PDF:
 * 1. Shelf Code Definitions - The named shelf configurations (A2, A3.1, B4, E2, etc.)
 * 2. Shelf Code Articles - The article numbers (BOM line items) that make up each shelf code
 *
 * Together with the planogram templates (phase2-config-schema.ts), this data enables
 * computing a full Bill of Materials for any blueprint by resolving:
 *   assignment → planogram template → shelf code → articles
 *
 * @see docs/BillingOfMaterials/ImportGuide.md
 */

import { createId } from "@paralleldrive/cuid2"
import { relations, sql } from "drizzle-orm"
import {
  bigint,
  boolean,
  check,
  foreignKey,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  unique,
  uniqueIndex,
} from "drizzle-orm/pg-core"
import type {
  MaterialRuleAction,
  MaterialRulePlacement,
  MaterialRuleScope,
  MaterialSetup,
} from "../features/bill-of-materials/core/materialRules"
import type { SchemaShelveType } from "../openapi-schema"
import { countries, countryCodeMappings } from "./app-schema"
import { configurationVersions } from "./store-configuration-schema"

// =============================================================================
// Shelf Code Definitions
// =============================================================================

/**
 * Height class for shelf codes
 * L = Low (1.44m), H = High (2.04m)
 */
export type ShelfCodeHeightClass = "L" | "H"

/**
 * Shelf code racking type
 */
export type ShelfCodeType = "gondola" | "wall" | "end-cap" | "storage" | "office"

/**
 * Shelf code definitions from the Racking Guidelines PDF
 *
 * Each shelf code (e.g., "A3.1", "B4", "E2") represents a specific racking
 * configuration with a defined set of articles (materials) needed to build it.
 *
 * Codes follow a lettered classification:
 * - A: Pure shelves (A2 = 2 shelves, A7 = 7 shelves)
 * - B: Crossbars + shelves
 * - D: Baskets + shelves
 * - E: Baskets + crossbars + shelves
 * - F: Price signings + shelves
 * - G: Divider (bedding)
 * - H: Driptray
 * - I: Flowers
 * - J: Divider (fashion)
 * - L: Impulse
 *
 * Sub-codes (.1, .2, etc.) indicate different shelf height spacing
 * for the same component count.
 */
export const shelfCodeDefinitions = pgTable(
  "shelf_code_definitions",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    /** The shelf code, e.g. "A3.1", "B4", "E2", "J" */
    code: text().notNull(),
    /** Low (1.44m) or High (2.04m) */
    heightClass: text("height_class").$type<ShelfCodeHeightClass>(),
    /** Racking type: gondola, wall, end-cap, storage, office */
    type: text().$type<ShelfCodeType>(),
    /** Number of shelves in this configuration */
    shelfCount: integer("shelf_count"),
    /** Human-readable name */
    displayName: text("display_name"),
    /** Optional description */
    description: text(),
    /** Shelving configuration: which fixture goes at which height (from PDF). Optional per-position colorHex overrides the global material color. */
    shelvingConfiguration:
      jsonb("shelving_configuration").$type<
        { heightCm: number; material: string; colorHex?: string | null }[]
      >(),
    /** Template-code color (#rrggbb, no leading #) extracted from the PDF. */
    colorHex: text("color_hex"),
    /** PDF page number this definition was extracted from (for provenance) */
    sourcePage: integer("source_page"),
    /** Setup variant: the code of the same version this is a variant of. Null = a standard code. */
    variantOfId: text("variant_of_id"),
    /** Setup variant: the setups it is for, in MATERIAL_SETUPS order. Empty for a standard code. */
    variantSetups: text("variant_setups")
      .array()
      .$type<MaterialSetup[]>()
      .notNull()
      .default(sql`'{}'::text[]`),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "shelf_code_definitions_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.variantOfId],
      foreignColumns: [table.id],
      name: "shelf_code_definitions_variant_of_fk",
    })
      .onUpdate("cascade")
      .onDelete("no action"),
    check(
      "shelf_code_definitions_variant_fields",
      sql`(${table.variantOfId} IS NULL) = (cardinality(${table.variantSetups}) = 0)`,
    ),
    uniqueIndex("shelf_code_definitions_variant_unique")
      .on(table.variantOfId, table.variantSetups)
      .where(sql`${table.variantOfId} IS NOT NULL`),
    unique("shelf_code_definitions_unique").on(table.configVersionId, table.code),
    index("shelf_code_definitions_version_idx").on(table.configVersionId),
    index("shelf_code_definitions_code_idx").on(table.code),
  ],
)

// =============================================================================
// Shelf Code Articles (BOM Line Items)
// =============================================================================

/**
 * Articles (materials) that make up a shelf code
 *
 * Each shelf code definition has a list of articles with quantities.
 * For example, shelf code "A3.1" might need:
 * - 2x Back panel 40x100 cm (art. 9704106)
 * - 1x Back panel 10x100 cm (art. 9704102)
 * - 3x Shelf 47x100 cm (art. 9704119)
 * - 1x Shelf 57x100 cm (art. 9704121)
 * - etc.
 *
 * Some articles have "1+" quantities in the PDF, meaning "1 per meter" —
 * these are marked with isPerMeter=true and should be multiplied by the
 * number of meters in the gondola when computing the final BOM.
 */
export const shelfCodeArticles = pgTable(
  "shelf_code_articles",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    shelfCodeId: text("shelf_code_id").notNull(),
    /** Article number, e.g. "9704119" */
    articleNumber: text("article_number").notNull(),
    /** Article description, e.g. "Shelf 47x100 cm" */
    description: text().notNull(),
    /** Quantity needed for this shelf code */
    quantity: integer().notNull().default(1),
    /** If true, quantity is "per meter" — multiply by gondola meters */
    isPerMeter: boolean("is_per_meter").notNull().default(false),
    /** Display order within the shelf code */
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.shelfCodeId],
      foreignColumns: [shelfCodeDefinitions.id],
      name: "shelf_code_articles_shelf_code_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    // No unique constraint: same article number can appear multiple times
    // per shelf code with different descriptions (e.g., 9704736 = Flowerbar 130 and Flowerbar 230)
    index("shelf_code_articles_shelf_code_idx").on(table.shelfCodeId),
    index("shelf_code_articles_article_nr_idx").on(table.articleNumber),
  ],
)

// =============================================================================
// Drizzle Relations
// =============================================================================

export const shelfCodeDefinitionsRelations = relations(shelfCodeDefinitions, ({ one, many }) => ({
  configVersion: one(configurationVersions, {
    fields: [shelfCodeDefinitions.configVersionId],
    references: [configurationVersions.id],
  }),
  articles: many(shelfCodeArticles),
}))

export const shelfCodeArticlesRelations = relations(shelfCodeArticles, ({ one }) => ({
  shelfCode: one(shelfCodeDefinitions, {
    fields: [shelfCodeArticles.shelfCodeId],
    references: [shelfCodeDefinitions.id],
  }),
}))

// =============================================================================
// Shelf Material Colors
// =============================================================================

/**
 * Color associated with a shelf material string (e.g. "Shelf 47", "Shelf 57",
 * "Basket 47 cm"), extracted from the racking-guidelines PDF. One row per
 * (configVersionId, material). Consumed by the LoM "Per Meter" Excel export
 * to tint each shelf cell with its material's color.
 */
export const shelfMaterialColors = pgTable(
  "shelf_material_colors",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    /** Material string as it appears in shelvingConfiguration (e.g. "Shelf 47"). */
    material: text().notNull(),
    /** Color as 6-char hex without leading "#" (e.g. "47D359"). */
    colorHex: text("color_hex").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "shelf_material_colors_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("shelf_material_colors_unique").on(table.configVersionId, table.material),
    index("shelf_material_colors_version_idx").on(table.configVersionId),
  ],
)

export const shelfMaterialColorsRelations = relations(shelfMaterialColors, ({ one }) => ({
  configVersion: one(configurationVersions, {
    fields: [shelfMaterialColors.configVersionId],
    references: [configurationVersions.id],
  }),
}))

// =============================================================================
// Fixture Type Articles (BOM line items for non-shelf fixtures)
// =============================================================================

/**
 * Articles linked directly to a non-shelf fixture type (e.g. battery_small,
 * pallet, bulk_pallet, promo_pallet, sustainability_gondola_end, ...).
 *
 * Unlike `shelfCodeArticles` (which sits behind a meter-keyed planogram
 * template lookup), these articles are resolved by simply counting fixture
 * occurrences on the canvas: for each placed fixture of a given `shelveType`,
 * multiply each linked article's `quantity` by the count and add to the BOM.
 *
 * Used for fixture types that don't fit the wall/gondola template model:
 * batteries, pallets, bulk displays, promo/season/drink pallets, gondola
 * ends, and similar standalone fixtures.
 */
export const fixtureTypeArticles = pgTable(
  "fixture_type_articles",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    /** ShelveType (e.g. "battery_small", "bulk_pallet", "promo_pallet") */
    shelveType: text("shelve_type").$type<SchemaShelveType>().notNull(),
    /** Article number, e.g. "9704119" */
    articleNumber: text("article_number").notNull(),
    /** Article description, e.g. "Battery Small Base" */
    description: text().notNull(),
    /** Quantity needed per occurrence of this fixture type */
    quantity: integer().notNull().default(1),
    /** Store conditions (BLU-768): null / empty = every store. */
    storeVersion: text("store_version"),
    countryId: text("country_id"),
    regionCode: text("region_code"),
    storeSizes: text("store_sizes")
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    /** Display order within the fixture type */
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "fixture_type_articles_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.countryId],
      foreignColumns: [countries.id],
      name: "fixture_type_articles_country_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
    foreignKey({
      columns: [table.regionCode],
      foreignColumns: [countryCodeMappings.displayCode],
      name: "fixture_type_articles_region_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
    index("fixture_type_articles_version_idx").on(table.configVersionId),
    index("fixture_type_articles_type_idx").on(table.shelveType),
  ],
)

export const fixtureTypeArticlesRelations = relations(fixtureTypeArticles, ({ one }) => ({
  configVersion: one(configurationVersions, {
    fields: [fixtureTypeArticles.configVersionId],
    references: [configurationVersions.id],
  }),
}))

// =============================================================================
// Fixture Type → Shelf Code links
// =============================================================================

/**
 * Link table allowing a non-shelf fixture type (e.g. battery_small) to
 * inherit the article list of an existing shelf code definition.
 *
 * In the BOM compute, for each placed fixture of `shelveType`, the articles
 * from every linked shelf code's article list are added (in addition to any
 * direct `fixture_type_articles` rows). `isPerMeter` is ignored — fixtures
 * are per-instance, so each article's `quantity` is multiplied by the
 * fixture count just like a direct fixture-type article.
 */
export const fixtureTypeShelfCodes = pgTable(
  "fixture_type_shelf_codes",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    shelveType: text("shelve_type").$type<SchemaShelveType>().notNull(),
    shelfCodeId: text("shelf_code_id").notNull(),
    /** Store conditions (BLU-782): null / empty = every store. */
    storeVersion: text("store_version"),
    countryId: text("country_id"),
    regionCode: text("region_code"),
    storeSizes: text("store_sizes")
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "fixture_type_shelf_codes_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.shelfCodeId],
      foreignColumns: [shelfCodeDefinitions.id],
      name: "fixture_type_shelf_codes_shelf_code_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.countryId],
      foreignColumns: [countries.id],
      name: "fixture_type_shelf_codes_country_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
    foreignKey({
      columns: [table.regionCode],
      foreignColumns: [countryCodeMappings.displayCode],
      name: "fixture_type_shelf_codes_region_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
    index("fixture_type_shelf_codes_version_idx").on(table.configVersionId),
    index("fixture_type_shelf_codes_type_idx").on(table.shelveType),
    index("fixture_type_shelf_codes_code_idx").on(table.shelfCodeId),
  ],
)

export const fixtureTypeShelfCodesRelations = relations(fixtureTypeShelfCodes, ({ one }) => ({
  configVersion: one(configurationVersions, {
    fields: [fixtureTypeShelfCodes.configVersionId],
    references: [configurationVersions.id],
  }),
  shelfCode: one(shelfCodeDefinitions, {
    fields: [fixtureTypeShelfCodes.shelfCodeId],
    references: [shelfCodeDefinitions.id],
  }),
}))

// =============================================================================
// 1.25m Shelf Articles (BOM line items applied per 1.25m shelf)
// =============================================================================

/**
 * Articles that apply to every 1.25m-wide shelf placed on the canvas
 * (`DetectedShelf.is125 === true`), regardless of fixture type or
 * merchandise group.
 *
 * In the BOM compute, each 1.25m shelf REPLACES one normal meter from its
 * merchandise group's meter loop and the article list defined here is added
 * once per 1.25m shelf instead. Example: a category with 12 assigned meters
 * where 1 meter is a 1.25m shelf produces 11 meters of normal shelf-code
 * articles + 1× this article list.
 */
export const shelf125Articles = pgTable(
  "shelf_125_articles",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    /** Article number, e.g. "9704119" */
    articleNumber: text("article_number").notNull(),
    /** Article description, e.g. "1.25m shelf base" */
    description: text().notNull(),
    /** Quantity needed per 1.25m shelf placed */
    quantity: integer().notNull().default(1),
    /** Display order */
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "shelf_125_articles_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("shelf_125_articles_unique").on(table.configVersionId, table.articleNumber),
    index("shelf_125_articles_version_idx").on(table.configVersionId),
  ],
)

export const shelf125ArticlesRelations = relations(shelf125Articles, ({ one }) => ({
  configVersion: one(configurationVersions, {
    fields: [shelf125Articles.configVersionId],
    references: [configurationVersions.id],
  }),
}))

// =============================================================================
// LoM Extras (Additional Materials)
// =============================================================================

/**
 * Catalog of "additional materials" — line items that aren't tied to a shelf
 * code, fixture type, or 1.25m shelf (e.g. navigation blocks, signage). One
 * row per article number per configuration version.
 *
 * Quantities are global per configuration version (`defaultQuantity`). At LoM
 * generation, the dialog pre-fills each row with the default and the planner
 * can override the value transiently for the current download without
 * mutating the catalog.
 */
export const lomExtras = pgTable(
  "lom_extras",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    /** Article number, e.g. "9704119" */
    articleNumber: text("article_number").notNull(),
    /** Article description, e.g. "Navigation Block A" */
    description: text().notNull(),
    /** Global qty applied to the LoM unless overridden by a matching Quantity rule. */
    defaultQuantity: integer("default_quantity").notNull().default(1),
    /**
     * If true, this material is included for stores with `stores.version = "1.0"`.
     * If false, the material is skipped entirely for v1.0 stores. Default true.
     * See `appliesToStore` in `bomComputation.ts`.
     */
    appliesToV1: boolean("applies_to_v1").notNull().default(true),
    /** Same as `appliesToV1` but for `stores.version = "2.0"`. */
    appliesToV2: boolean("applies_to_v2").notNull().default(true),
    /**
     * @deprecated Replaced by `multiplyBy` (BLU-762). Kept in sync on write
     * ("checkouts" = true) until the column can be dropped.
     */
    perCashier: boolean("per_cashier").notNull().default(false),
    /**
     * What the resolved qty is counted by (`ExtraMultiplier`): a
     * store-information count (checkouts, trolleys, ...) or a blueprint count
     * (meters, 1.25 m meters, "fixture:end_cap", ...), or null for a fixed
     * amount per store.
     */
    multiplyBy: text("multiply_by"),
    /**
     * With `multiplyBy`: "each" gives the amount for each of the count,
     * "ifAny" gives it once when the store has at least one (BLU-767).
     */
    multiplyMode: text("multiply_mode").notNull().default("each"),
    /** Display order (legacy; not exposed in the catalog UI). */
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "lom_extras_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("lom_extras_unique").on(table.configVersionId, table.articleNumber),
    index("lom_extras_version_idx").on(table.configVersionId),
  ],
)

export const lomExtrasRelations = relations(lomExtras, ({ one, many }) => ({
  configVersion: one(configurationVersions, {
    fields: [lomExtras.configVersionId],
    references: [configurationVersions.id],
  }),
  quantityRules: many(lomExtraQuantityRules),
}))

/**
 * Per-(country, store-sizes) quantity rules for a `lomExtras` row.
 *
 * At LoM generation, the resolver picks the most-specific matching row:
 *   - `countryId` matches the project store's country, or is NULL (wildcard);
 *   - `storeSizes` is empty (= any size), or contains the project store's
 *     size label.
 *
 * Most-specific = both dimensions set > country only > size only > both
 * wildcard, with createdAt as tiebreaker. Falls back to
 * `lomExtras.defaultQuantity` when no row matches.
 *
 * Version filtering happens upstream of the resolver via `lomExtras.appliesToV1`
 * / `appliesToV2` — materials disabled for the project store's version are
 * dropped before rule resolution runs.
 */
export const lomExtraQuantityRules = pgTable(
  "lom_extra_quantity_rules",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    lomExtraId: text("lom_extra_id").notNull(),
    /** Null = any country (wildcard). */
    countryId: text("country_id"),
    /**
     * One region of `countryId` (`country_code_mappings.display_code`, e.g.
     * "BE-WA"), or null for the whole country (BLU-764). A region rule wins
     * over its country's.
     */
    regionCode: text("region_code"),
    /** Empty array = any size. */
    storeSizes: text("store_sizes")
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    quantity: integer().notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.lomExtraId],
      foreignColumns: [lomExtras.id],
      name: "lom_extra_qty_rules_extra_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.countryId],
      foreignColumns: [countries.id],
      name: "lom_extra_qty_rules_country_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
    foreignKey({
      columns: [table.regionCode],
      foreignColumns: [countryCodeMappings.displayCode],
      name: "lom_extra_qty_rules_region_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
    index("lom_extra_qty_rules_extra_idx").on(table.lomExtraId),
    index("lom_extra_qty_rules_country_idx").on(table.countryId),
  ],
)

export const lomExtraQuantityRulesRelations = relations(lomExtraQuantityRules, ({ one }) => ({
  lomExtra: one(lomExtras, {
    fields: [lomExtraQuantityRules.lomExtraId],
    references: [lomExtras.id],
  }),
  country: one(countries, {
    fields: [lomExtraQuantityRules.countryId],
    references: [countries.id],
  }),
}))

// =============================================================================
// Material rules (BLU-725)
// =============================================================================

/**
 * Rules that change a meter's (or a run's) article list based on its setup and
 * its store: replace, remove, add or set the quantity of an article. One table
 * per configuration version, so 1.25 m, Small Bottom Shelf and Low Wall combine
 * without a shelf code for every combination.
 *
 * Every condition column is optional (null / empty = any); every filled-in one
 * must match. See `features/bill-of-materials/core/materialRules.ts` for the
 * engine and the save-time checks.
 */
export const lomMaterialRules = pgTable(
  "lom_material_rules",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    action: text().$type<MaterialRuleAction>().notNull(),
    /** replace / remove / setQuantity: the article matched. add: the article added. */
    articleNumber: text("article_number").notNull(),
    /** replace only: the article it becomes. */
    toArticleNumber: text("to_article_number"),
    /** add: how many. setQuantity: the new quantity. replace: factor (null = 1). */
    quantity: bigint({ mode: "number" }),
    scope: text().$type<MaterialRuleScope>().notNull().default("meter"),
    /** Setups the meter must all have: smallBottomShelf / lowWall / w125. Empty = any. */
    setups: text()
      .array()
      .$type<MaterialSetup[]>()
      .notNull()
      .default(sql`'{}'::text[]`),
    placement: text().$type<MaterialRulePlacement>(),
    shelfCode: text("shelf_code"),
    groupCode: text("group_code"),
    storeVersion: text("store_version"),
    countryId: text("country_id"),
    /** One region of `countryId` (e.g. "BE-WA"), or null for the whole country (BLU-768). */
    regionCode: text("region_code"),
    /** Store sizes the rule holds for; empty = any size (BLU-768). */
    storeSizes: text("store_sizes")
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    note: text(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "lom_material_rules_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.countryId],
      foreignColumns: [countries.id],
      name: "lom_material_rules_country_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
    foreignKey({
      columns: [table.regionCode],
      foreignColumns: [countryCodeMappings.displayCode],
      name: "lom_material_rules_region_fk",
    })
      .onUpdate("cascade")
      .onDelete("restrict"),
    index("lom_material_rules_version_idx").on(table.configVersionId),
  ],
)

export const lomMaterialRulesRelations = relations(lomMaterialRules, ({ one }) => ({
  configVersion: one(configurationVersions, {
    fields: [lomMaterialRules.configVersionId],
    references: [configurationVersions.id],
  }),
  country: one(countries, {
    fields: [lomMaterialRules.countryId],
    references: [countries.id],
  }),
}))

// =============================================================================
// Article list (BLU-730)
// =============================================================================

/** How a creme (1.0) store orders an article. */
export const ARTICLE_CREME_MODES = ["toFillIn", "same", "own"] as const
export type ArticleCremeMode = (typeof ARTICLE_CREME_MODES)[number]

/**
 * Every article a configuration version can order, in one place (BLU-730).
 * Shelf codes, fixture types, the 1.25 m list and the additional materials each
 * keep their own rows; this list holds what is true of the article itself: its
 * description, what a creme (1.0) store orders, and whether a run needs one
 * extra of it.
 */
export const lomArticles = pgTable(
  "lom_articles",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    articleNumber: text("article_number").notNull(),
    description: text().notNull(),
    /**
     * toFillIn: nobody decided yet, so a creme store gets 000000000 with a flag.
     * same: a creme store orders this same article number.
     * own: a creme store orders `cremeArticleNumber` instead (BLU-706).
     */
    cremeMode: text("creme_mode").$type<ArticleCremeMode>().notNull().default("toFillIn"),
    cremeArticleNumber: text("creme_article_number"),
    cremeDescription: text("creme_description"),
    /**
     * Whether a run of meters needs one extra of this article, for the "1+"
     * parts in the Racking Guidelines (BLU-729). Null means nobody decided yet;
     * the list of materials treats null the same as false.
     */
    extraPerRun: boolean("extra_per_run"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "lom_articles_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("lom_articles_unique").on(table.configVersionId, table.articleNumber),
    index("lom_articles_version_idx").on(table.configVersionId),
    // The creme fields belong to "own" and to nothing else, so a mode change
    // can never leave an old code behind.
    check(
      "lom_articles_creme_fields",
      sql`(${table.cremeMode} = 'own') = (${table.cremeArticleNumber} IS NOT NULL)
        AND (${table.cremeDescription} IS NULL OR ${table.cremeArticleNumber} IS NOT NULL)`,
    ),
  ],
)

export const lomArticlesRelations = relations(lomArticles, ({ one }) => ({
  configVersion: one(configurationVersions, {
    fields: [lomArticles.configVersionId],
    references: [configurationVersions.id],
  }),
}))
